/**
 * What a month's revenue figure MEANS on a plan with a Contracted Fee (RP-4.1, RP-D16).
 * Pure, no I/O — shared by the plan math on the server and the grid's draft footer, so
 * the two can never answer the question differently.
 *
 * Three answers, by Pricing Model × fee basis:
 *
 * - **indicative** — hours × bill rate. Every T&M plan, including a T&M × Total plan
 *   whose fee is a not-to-exceed ceiling: that work is billed as it is done, so the
 *   hours' value IS the month's revenue.
 * - **monthly-fee** — the Contracted Fee itself, every month. A Monthly-basis fee is a
 *   per-month figure (a managed service or subscription), so it is not allocated at all.
 * - **fee-spread** — Fixed Price × Total. The fee is one figure for the whole plan, so a
 *   month's share is the fee spread in proportion to that month's EFFECTIVE hours. This is
 *   a forecast, not a billing schedule — we do not model one — and it is deliberately not
 *   called "Earned Value", which in earned-value accounting means work actually performed
 *   and needs actuals.
 */

export type MonthRevenueBasis = "indicative" | "monthly-fee" | "fee-spread";

export function monthRevenueBasis(input: {
  pricingModel: string;
  contractedFee: string | null;
  contractedFeeBasis: string | null;
}): MonthRevenueBasis {
  if (input.contractedFee === null) return "indicative";
  if (input.contractedFeeBasis === "Monthly") return "monthly-fee";
  return input.pricingModel === "FixedPrice" ? "fee-spread" : "indicative";
}

/**
 * The fee the margin of record is measured on, across the plan's months.
 *
 * A Monthly-basis fee is per month, so the plan earns it once per month in the window
 * (the Planning Horizon for an evergreen plan). A Total-basis fee is already the whole.
 */
export function feeOfRecordMinor(
  feeMinor: number,
  contractedFeeBasis: string | null,
  monthCount: number,
): number {
  return contractedFeeBasis === "Monthly" ? feeMinor * monthCount : feeMinor;
}

/**
 * Split `totalMinor` across `weights` in proportion, so the parts sum to the total EXACTLY.
 *
 * Largest-remainder: every part takes its floor, then the leftover minor units go one each
 * to the parts with the largest fractional remainder (earliest wins a tie). Products run in
 * BigInt because a `numeric(14,2)` fee in cents times a plan's hours in hundredths passes
 * 2^53 well before either number looks large.
 *
 * Null when every weight is zero — a plan with no hours has no basis to spread a fee on,
 * and inventing an even split would be a billing schedule by another name.
 */
export function spreadByWeights(totalMinor: number, weights: readonly number[]): number[] | null {
  const sum = weights.reduce((acc, w) => acc + w, 0);
  if (sum <= 0) return null;

  const total = BigInt(totalMinor);
  const denominator = BigInt(sum);
  const parts = weights.map((w) => {
    const numerator = total * BigInt(w);
    return { base: numerator / denominator, remainder: numerator % denominator };
  });

  let leftover = Number(total - parts.reduce((acc, p) => acc + p.base, 0n));
  const order = parts
    .map((p, index) => ({ index, remainder: p.remainder }))
    .sort((a, b) =>
      a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
    );
  const result = parts.map((p) => Number(p.base));
  for (const { index } of order) {
    if (leftover <= 0) break;
    result[index] = (result[index] ?? 0) + 1;
    leftover -= 1;
  }
  return result;
}

/**
 * Each month's revenue of record, in CENTS, from the month totals a plan view already
 * carries — for a frozen version snapshotted before RP-29 added the figure. The live path
 * computes the same thing in computePlanMetrics, at Micros, from the cells.
 *
 * `revenueCents` is hours × rate (the T&M answer); `hoursCenti` is EFFECTIVE hours, the
 * spread's weights. Null where a Fixed Price × Total plan has no hours to spread on.
 */
export function monthRevenueOfRecordCents(input: {
  pricingModel: string;
  contractedFee: string | null;
  contractedFeeBasis: string | null;
  feeCents: number | null;
  months: ReadonlyArray<{ hoursCenti: number; revenueCents: number }>;
}): Array<number | null> {
  const basis = monthRevenueBasis(input);
  const fee = input.feeCents;
  if (basis === "indicative" || fee === null) return input.months.map((m) => m.revenueCents);
  if (basis === "monthly-fee") return input.months.map(() => fee);
  const spread = spreadByWeights(
    fee,
    input.months.map((m) => m.hoursCenti),
  );
  return input.months.map((_, i) => spread?.[i] ?? null);
}
